import { Auth0Provider } from "@auth0/auth0-react";
import { StrictMode } from "react";
import type ReactDOM from "react-dom/client";
import { createRoot } from "react-dom/client";
import { App, type AppProps } from "@/App";
import { ContainerApp } from "./container/ContainerApp.tsx";
import { name as appName } from "./package.json";

import "@/index.css";
import {
  insertHostFields,
  isInShadowDom,
  setPaymentButtonsInShadowDom,
} from "@/utils/shadow-dom";

type Props = {
  container: HTMLElement;
} & Record<string, unknown>;

let root: ReactDOM.Root;

// standalone entrypoint for SPA with login container
if (!window.__POWERED_BY_QIANKUN__) {
  // Mark standalone mode. The cashier owns its Auth0 session here (refresh token),
  // so withdrawal 2FA uses the in-browser SPA flow. Embedded builds never run this
  // file, so the marker stays unset and the host-mint flow is used instead.
  window.__CASHIER_STANDALONE__ = true;
  // biome-ignore lint/style/noNonNullAssertion: root element exists in index.html
  root = createRoot(document.getElementById("root")!);
  root.render(
    <StrictMode>
      <Auth0Provider
        domain={import.meta.env.VITE_AUTH0_DOMAIN}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
        useRefreshTokens={true}
        useRefreshTokensFallback={true}
        authorizationParams={{
          redirect_uri: `${window.location.origin}/login/callback`,
          scope: "openid profile email offline_access",
          audience: "https://api.pepperstone.com",
        }}
        cacheLocation="localstorage"
      >
        <ContainerApp />
      </Auth0Provider>
    </StrictMode>
  );
}

function getAppProps(props: Props): AppProps {
  return {
    locale: (props.locale as string | undefined) ?? "en",
    token: (props.token as string | undefined) ?? "",
  };
}

// entrypoint for qiankun microsite
/**
 * The bootstrap will only be called once when the child application is initialized.
 * The next time the child application re-enters, the mount hook will be called directly, and bootstrap will not be triggered repeatedly.
 * Usually we can do some initialization of global variables here,
 * such as application-level caches that will not be destroyed during the unmount phase.
 */
export async function bootstrap() {
  console.log(`[${appName} mfe] bootstrap`);
}

/**
 * The mount method is called every time the application enters,
 * usually we trigger the application's rendering method here.
 */
export async function mount(props: Props) {
  console.log(`[${appName} mfe] mount`, { props });

  if (!root) {
    // biome-ignore lint/style/noNonNullAssertion: must exits
    const rootContainer = props.container.querySelector("#root")!;
    root = createRoot(rootContainer);

    if (isInShadowDom(rootContainer)) {
      const rootNode = rootContainer.getRootNode();
      if (rootNode instanceof ShadowRoot) {
        insertHostFields({ shadowHost: rootNode.host });
        setPaymentButtonsInShadowDom(true);
      }
    }
  }

  const appProps = getAppProps(props);
  root.render(<App {...appProps} />);
}

/**
 * Methods that are called each time the application is switched/unloaded,
 * usually in this case we uninstall the application instance of the subapplication.
 */
export async function unmount(props: Props) {
  console.log(`[${appName} mfe] unmount`, { props });

  if (root) {
    root.unmount();
    setPaymentButtonsInShadowDom(false);
  }
}

/**
 * Optional lifecycle，just available with loadMicroApp way
 */
export async function update(props: Props) {
  console.log(`[${appName} mfe] update`, { props });

  const appProps = getAppProps(props);
  if (!root) {
    console.error("root is not defined");
  } else {
    root.render(<App {...appProps} />);
  }
}
